import GuildContext, { useGuild } from 'app/frontend/base/config/context/guild'
import EntityMenu from 'app/frontend/base/components/EntityMenu'
import BannerImage from 'app/frontend/base/components/BannerImage'
import { Link } from 'react-router-dom'
import { useTheme } from '@/components/theme-provider'
import { useCustomDomain } from 'app/frontend/base/hooks/useCustomDomain'
import { useActionable } from 'app/frontend/base/hooks/useActionable'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { cn } from '@/lib/utils'
import { useGuildMenuItems } from '../../GuildDashboard/components/GuildHeader'
import {
  CustomDomainSelectedTeamMenu,
  CustomDomainTeamMenu,
  CustomDomainTeamSwitcher,
  DEFAULT_CUSTOM_DOMAIN_MAIN_MENU_CONTENT,
  getCustomDomainTeamsWithAccess,
  getSelectedCustomDomainTeam,
} from './CustomDomainTeamNavigation'

const CustomDomainSidebar = () => {
  const { currentScheme } = useTheme()
  const { customDomain } = useCustomDomain()
  const {
    data: { user },
  } = usePageInfo()
  const { currentAction } = useActionable(user)

  if (!customDomain) return null

  const headerLogoPath =
    currentScheme === 'dark'
      ? customDomain?.darkModeHeaderLogoPath
      : customDomain?.lightModeHeaderLogoPath

  return (
    <div className={cn('flex gap-2 flex-col', currentAction ? 'pt-8' : '')}>
      <Link
        to='/'
        className='flex flex-col items-center gap-2 shrink-0 min-w-0'
      >
        {headerLogoPath ? (
          <img src={headerLogoPath} className='w-full px-2' />
        ) : (
          <BannerImage
            className='w-12 h-12 min-w-12 min-h-12'
            faction={customDomain.guild.faction}
            fileName={customDomain.guild.bannerFilename}
            size='custom'
          />
        )}

        {customDomain.headerShowName ? (
          <span className='text-xl font-bold text-accent-foreground min-w-0 wrap-break-word text-center'>
            {customDomain.guild.name}
          </span>
        ) : null}
      </Link>

      <div className='flex flex-col gap-0'>
        {customDomain.team ? (
          <GuildContext.Provider
            value={{
              guild: customDomain.guild,
              guildReference: customDomain.guild,
              currentTeam: customDomain.team,
              teamReference: customDomain.team,
            }}
          >
            <CustomDomainTeamSidebar />
          </GuildContext.Provider>
        ) : (
          <GuildContext.Provider
            value={{
              guild: customDomain.guild,
              guildReference: customDomain.guild,
            }}
          >
            <CustomDomainGuildSidebar />
          </GuildContext.Provider>
        )}
      </div>
    </div>
  )
}

const CustomDomainTeamSidebar = () => {
  return <CustomDomainTeamMenu />
}

const CustomDomainGuildSidebar = () => {
  const { customDomain } = useCustomDomain()
  const mainMenuContent =
    customDomain?.headerConfig?.mainMenuContent ??
    DEFAULT_CUSTOM_DOMAIN_MAIN_MENU_CONTENT
  const selectedTeam = getSelectedCustomDomainTeam(customDomain)
  const showTeamSwitcher =
    mainMenuContent !== 'only_guild_menu' &&
    getCustomDomainTeamsWithAccess(customDomain).length > 0

  return (
    <GuildContext.Provider
      value={{
        guild: customDomain?.guild,
        guildReference: customDomain?.guild,
        teamReference: selectedTeam,
      }}
    >
      {showTeamSwitcher ? (
        <div className='mb-6'>
          <div className='px-1 text-xs font-medium text-muted-foreground'>
            Team
          </div>
          <CustomDomainTeamSwitcher
            selectedTeam={selectedTeam}
            className='h-9 border mt-1 rounded-md bg-card-nested px-3 w-full justify-between hover:text-accent-foreground transition-colors'
          />
        </div>
      ) : null}

      <CustomDomainGuildSidebarMenu mainMenuContent={mainMenuContent} />
    </GuildContext.Provider>
  )
}

const CustomDomainGuildSidebarMenu = ({
  mainMenuContent,
}: {
  mainMenuContent: wowaudit.CustomDomain['headerConfig']['mainMenuContent']
}) => {
  const menuItems = useGuildMenuItems()
  const { guild, guildReference, teamReference } = useGuild()
  const showTeamMenu =
    mainMenuContent === 'team_switcher_and_team_menu' && teamReference

  if (showTeamMenu) {
    return (
      <CustomDomainSelectedTeamMenu
        selectedTeam={teamReference}
        guild={guild}
        guildReference={guildReference}
      />
    )
  }

  if (!menuItems.length) return null

  return (
    <GuildContext.Provider value={{ guild, guildReference }}>
      <EntityMenu menuItems={menuItems} placement='header' />
    </GuildContext.Provider>
  )
}

export default CustomDomainSidebar
